import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Custodian } from '@models/review/custodian.model';
import { LexbeDoc } from '@models/doc.model';
import { CustodianService } from '@services/custodian.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AnalysisManager } from '@managers/analysis.manager';
import { Fact } from '@models/ui/fact.model';
import { Issue } from 'src/app/models/ui/issue.model';
import { LinkedCustodian } from '@models/ui/linked-custodian.model';
import { flatMap } from 'lodash-es';
import { LinkedDocument } from '@models/ui/linked-document.model';
import { BaseComponent } from 'src/app/components/base.component';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { NotificationManager } from '@managers/notification.manager';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { ReviewManager } from '@managers/review.manager';
import { OkCancelDialogNewComponent } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog-new.component';
import { OkCancelDialogModel } from '../../dialogs/ok-cancel-dialog-new/ok-cancel-dialog.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { documentViewActions } from '@store/doc-viewer/document-view/document-view.actions';

@Component({
	selector: 'lex-facts-and-issues-panel',
	templateUrl: './facts-and-issues-panel.component.html',
	styleUrls: ['./facts-and-issues-panel.component.scss'],
	standalone: false
})
export class FactsAndIssuesPanelComponent extends BaseComponent implements OnInit {
	public showCustodianSelect: boolean = false;
	public showIssuesSelect: boolean = false;
	public selectedFact: Fact = null;
	public selectedLinkedDocument: LinkedDocument = null;
	public docFacts: LinkedDocument[] = [];
	public facts: Fact[] = [];
	public factsNotInDoc: Fact[] = [];
	public caseCustodians: any[];
	public caseIssues: Issue[];
	public issuesNotInFact: Issue[];
	public custodiansNotInFact: Custodian[];
	public currentDocumentSnapshot: LexbeDoc;
	public sortBy: SortFactBy = '1';
	public editFactForm: FormGroup;

	public contestedList = [
		{ value: 3, displayValue: '' },
		{ value: 0, displayValue: 'No' },
		{ value: 1, displayValue: 'Yes, by us' },
		{ value: 2, displayValue: 'Yes, by them' },
		{ value: 4, displayValue: 'Unsure' },
		{ value: 5, displayValue: 'Prospective' }
	];

	constructor(
		private custodianService: CustodianService,
		private analysisManager: AnalysisManager,
		private notificationManager: NotificationManager,
		private dialog: MatDialog,
		private docViewerStore: DocViewerStore,
		private reviewManager: ReviewManager,
		private fb: FormBuilder,
		private store: Store
	) {
		super();
		this.docViewerStore.currentDocument$.pipe(
			withLatestFrom(this.docViewerStore.simpleState$),
			takeUntil(this.destroy$)
		).subscribe(([snapshot, state]) => {
			this.currentDocumentSnapshot = snapshot;
			this.caseCustodians = state.custodians;
			this.caseIssues = state.issues;
			this.facts = state.facts;
			this.docFacts = flatMap(this.facts, (fact) => {
				return fact.linkedDocuments.filter((linkedDocument) => linkedDocument.documentId === this.currentDocumentSnapshot.docId);
			});
			this.sortFacts();
			this.factsNotInDoc = this.facts;
			this.updateFilteredFormFields();
		});
	}

	public ngOnInit(): void {
		this.editFactForm = this.fb.group({
			title: ['', [Validators.required, Validators.maxLength(1024)]],
			pageNumber: [0, [
				Validators.min(0),
				Validators.max(this.currentDocumentSnapshot.common.pageCount)]]
		});

		this.editFactForm.valueChanges.subscribe((currentData) => {
			this.selectedFact.title = currentData.title;
			this.selectedLinkedDocument.pageNumber = currentData.pageNumber;
		});
	}

	public updateFilteredFormFields(): void {
		this.issuesNotInFact = this.caseIssues?.filter((caseIssue) => this.selectedFact?.linkedIssues?.every((linkedIssue) => linkedIssue.id !== caseIssue.id));
		this.custodiansNotInFact = this.caseCustodians?.filter((caseCustodians) => this.selectedFact?.linkedCustodians?.every((custodian) => custodian.id !== caseCustodians.custodianId));
	}

	public addDocToFactAndSelect(fact: Fact): void {
		this.selectedFact = this.facts?.find((f) => f.id === fact.id);
		this.buildLinkedDocument();
		this.selectedFact.linkedDocuments.push(this.selectedLinkedDocument);
		this.updateFilteredFormFields();
		this.fillForm();
	}

	public getContested(value: number): string {
		return this.contestedList.find(item => item.value === value).displayValue;
	}

	public sortDocFacts($event) {
		this.sortBy = $event.value;
		this.sortFacts();
	}

	public getFact(id: string): Fact {
		return this.facts.find((fact) => fact.id === id);
	}

	public getDocFactPageNumber(selectedFact: Fact): number {
		const result = this.docFacts.find((fact) => fact.factId === selectedFact.id);
		return result ? result.pageNumber : -1;
	}

	public newFactClicked(): void {
		this.buildFact();
		this.buildLinkedDocument();
		this.selectedFact.linkedDocuments.push(this.selectedLinkedDocument);
		this.updateFilteredFormFields();
		this.fillForm();
	}

	public buildFact(): void {
		const fact = new Fact();
		fact.caseId = this.currentDocumentSnapshot.caseId;
		fact.title = '';
		this.selectedFact = fact;
	}

	public appendIssueToSelectedFact($event: MatSelectChange): void {
		$event.source.writeValue(null);

		if (this.selectedFact.linkedIssues == null)
			this.selectedFact.linkedIssues = new Array<Issue>();

		this.selectedFact.linkedIssues.push($event.value);
		this.updateFilteredFormFields();
		this.showIssuesSelect = false;
	}

	public appendCustodianToSelectedFact($event: MatSelectChange): void {
		$event.source.writeValue(null);
		const legacyCustodian = $event.value as Custodian;
		const linkedCustodian = new LinkedCustodian();
		linkedCustodian.id = legacyCustodian.custodianId;
		linkedCustodian.name = legacyCustodian.name;
		this.selectedFact.linkedCustodians.push(linkedCustodian);
		this.updateFilteredFormFields();
		this.showCustodianSelect = false;
	}

	public detachIssueFromSelectedFact(issue: any): void {
		const index = this.selectedFact.linkedIssues.indexOf(issue, 0);
		if (index !== -1)
			this.selectedFact.linkedIssues.splice(index, 1);
		this.updateFilteredFormFields();

	}

	public detachCustodianFromSelectedFact(custodian: LinkedCustodian): void {
		const index = this.selectedFact.linkedCustodians.indexOf(custodian, 0);
		if (index !== -1)
			this.selectedFact.linkedCustodians.splice(index, 1);
		this.updateFilteredFormFields();
	}

	public editClicked(docFact: LinkedDocument): void {
		this.selectedLinkedDocument = docFact;
		this.selectedFact = this.getFact(docFact.factId);
		this.updateFilteredFormFields();
		this.fillForm();
	}

	public removeDocFact(docFact: LinkedDocument): void {
		const model = new OkCancelDialogModel();
		model.title = 'Confirm';
		model.message = 'Remove current document from fact? The fact will not be deleted.';
		const config = new MatDialogConfig<OkCancelDialogModel>();
		config.data = model;
		this.dialog.open(OkCancelDialogNewComponent, config).afterClosed().subscribe((confirmed) => {
			if (!confirmed) return;
			const targetFact = this.facts.find((fact) => fact.id === docFact.factId);
			targetFact.linkedDocuments = targetFact.linkedDocuments.filter((linkedDocument) => {
				return !(linkedDocument.factId === docFact.factId &&
					linkedDocument.documentId === docFact.documentId &&
					linkedDocument.pageNumber === docFact.pageNumber);
			});

			this.analysisManager.saveFact({ fact: targetFact }).subscribe(() => {
				this.reviewManager.refreshDocument();
				this.notificationManager.displaySnackBarMessage({ message: `Fact is no longer linked to this document.` });
			});
		});
	}

	public deleteFact(fact: Fact): void {
		const model = new OkCancelDialogModel();
		model.title = 'Confirm';
		model.message = 'Delete Fact?';
		const config = new MatDialogConfig<OkCancelDialogModel>();
		config.data = model;
		this.dialog.open(OkCancelDialogNewComponent, config).afterClosed().subscribe((confirmed) => {
			if (!confirmed) return;
			this.analysisManager.deleteFact({ id: fact.id }).subscribe(() => {
				this.reviewManager.refreshDocument();
			});
		});
	}

	public saveClicked(): void {
		this.analysisManager.saveFact({ fact: this.selectedFact }).subscribe(() => {
			this.reviewManager.refreshDocument();
			this.clearSelected();
			this.notificationManager.displaySnackBarMessage({ message: `Fact Saved Successfully.` });
		});
	}

	public clearSelected(): void {
		this.selectedLinkedDocument = null;
		this.selectedFact = null;
	}

	public updateFact(fact: string): void {
		this.selectedFact.title = fact;
	}

	private buildLinkedDocument(): void {
		const linkedDocument = new LinkedDocument();
		linkedDocument.caseId = this.selectedFact.caseId;
		linkedDocument.documentId = this.currentDocumentSnapshot.docId;
		linkedDocument.factId = this.selectedFact.id;
		linkedDocument.pageNumber = 0;
		this.selectedLinkedDocument = linkedDocument;
	}

	public viewPage(pageNumber: number): void {
		this.reviewManager.goToPage(pageNumber);
		this.store.dispatch(documentViewActions.viewUpdated({ selectedView: 'pages' }));
	}

	private sortFacts(): void {
		this.docFacts.sort((a, b) => {
			if (this.sortBy === '1') {
				return (new Date(this.getFact(a.factId).dateStart)).getTime() - (new Date(this.getFact(b.factId).dateStart)).getTime();
			} else {
				return a.pageNumber - b.pageNumber;
			}
		});
	}

	private fillForm(): void {
		this.editFactForm.setValue({
			title: this.selectedFact.title,
			pageNumber: this.selectedLinkedDocument.pageNumber
		});
	}
}
export type SortFactBy = '1' | '2';
